/**
 * Process 6 (Medicaid benefits lost): the re-vets due now, in the PA's own work.
 *
 * One section, shown only when the server lists items. Each item carries a
 * small panel to record the outcome, offered only where the scope allows a
 * status change. Self-contained: it reads and writes through two advocateApi
 * calls and touches nothing else in the shell.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { advocateApi, freshKey, shortDate, RevetItem, RevetOutcome } from './advocateApi';

const OUTCOME_SAYS: Record<RevetOutcome, { label: string; says: string }> = {
  // Ruling 2026-10-13: requalified is a continuation of the same sale -- the UMED team resubmits the terms; no new lead.
  REQUALIFIED: { label: 'Requalified', says: 'A correction to resubmit the terms opens with UMED on the same UMED. Nothing else is needed from you.' },
  DNQ: { label: 'Does not qualify', says: 'The UMED is marked NO LONGER QUALIFIES. Finance is told. No amount changes.' },
};

export function dueWords(i: Pick<RevetItem, 'revet_due_on' | 'days_until_due' | 'overdue'>): string {
  if (i.overdue) { const n = Math.abs(i.days_until_due); return `overdue ${n} ${n === 1 ? 'day' : 'days'}`; }
  return `re-vet due ${shortDate(i.revet_due_on)}`;
}

function OutcomePanel({ item, onDone }: { item: RevetItem; onDone: (msg: string) => void }) {
  const [outcome, setOutcome] = useState<RevetOutcome | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(freshKey);
  const noteOk = note.trim().length >= 3;
  return (
    <form className="pa-form" aria-label={`Record the re-vet outcome for ${item.resident_name}`} onSubmit={async (e) => {
      e.preventDefault();
      if (!outcome || busy) return;
      if (!noteOk) { setErr('A note is required (at least 3 characters).'); return; }
      setBusy(true); setErr(null);
      try {
        const r = await advocateApi.recordRevetOutcome(item.id, { outcome, note: note.trim() }, key);
        if (!r || !r.success) {
          setErr(r && r.status !== undefined ? (r.error || r.message || 'That did not go through.') : 'Could not reach the server. Nothing was changed.');
          return;
        }
        setKey(freshKey());
        // The server's own confirmation ("Requalified. A correction to resubmit the terms is open with UMED; ...").
        onDone(r.data?.replayed ? 'That outcome was already recorded.' : (r.data?.message || `Recorded: ${OUTCOME_SAYS[outcome].label}. ${OUTCOME_SAYS[outcome].says}`));
      } catch { setErr('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Outcome</legend>
        {(['REQUALIFIED', 'DNQ'] as RevetOutcome[]).map((o) => (
          <label key={o} style={{ flexDirection: 'row', gap: 6, alignItems: 'baseline' }}>
            <input type="radio" name={`revet-${item.id}`} checked={outcome === o} onChange={() => { setOutcome(o); setErr(null); }} />
            <span style={{ textTransform: 'none', letterSpacing: 0 }}>{OUTCOME_SAYS[o].label}</span>
          </label>
        ))}
      </fieldset>
      {outcome && <p className="pa-notice">{OUTCOME_SAYS[outcome].says}</p>}
      <label><span>Note (required)</span>
        <textarea value={note} rows={2} aria-label="Re-vet note" onChange={(e) => { setNote(e.target.value); setErr(null); }} />
      </label>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
      <div className="btns"><button type="submit" className="pa-open" disabled={!outcome || busy}>{busy ? 'Recording…' : 'Record outcome'}</button></div>
    </form>
  );
}

export default function RevetsDue({ as, canRecord, onChanged }: { as?: string | null; canRecord: boolean; onChanged?: () => void }) {
  // Items belong to the `as` they were loaded for: a manager switching advocates must never see the previous
  // advocate's residents under the new workspace, even when the new load fails.
  const scope = as || '';
  const [snap, setSnap] = useState<{ scope: string; items: RevetItem[] }>({ scope, items: [] });
  const items = snap.scope === scope ? snap.items : [];
  const [open, setOpen] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // A failed load is not "none due": keep what was last known FOR THIS SCOPE and say so, so overdue work is never hidden by an outage.
  const [failedScope, setFailedScope] = useState<string | null>(null);
  const loadFailed = failedScope === scope;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  // Every load gets a number; only the latest request's answer is applied, so overlapping retries that finish out of
  // order can neither re-show an old failure over a newer success nor overwrite a fresher list with an older one.
  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const mine = as || '';
    const seq = ++seqRef.current;
    const current = () => seq === seqRef.current && scopeRef.current === mine;
    try {
      const r = await advocateApi.revets(as);
      if (!current()) return; // a superseded request, or a scope the viewer has left
      if (r && r.success && r.data && Array.isArray(r.data.items)) { setSnap({ scope: mine, items: r.data.items }); setFailedScope(null); }
      else setFailedScope(mine);
    } catch { if (current()) setFailedScope(mine); }
  }, [as]);
  useEffect(() => { load(); }, [load]);
  if (items.length === 0 && !said && !loadFailed) return null;
  return (
    <section className="pa-revets" aria-label="Re-vets due">
      <h2>Re-vets due</h2>
      <p className="why">Medicaid benefits were lost for these residents. Each is checked again 90 days after the loss.</p>
      {said && <p className="pa-notice ok" role="status">{said}</p>}
      {loadFailed && (
        <p className="pa-notice stop" role="alert">
          {items.length > 0 ? 'The re-vets due could not be refreshed. The list below may be out of date.' : 'The re-vets due could not be loaded. Some may be due or overdue.'}
          {' '}<button type="button" className="pa-link" onClick={() => { load(); }}>Try again</button>
        </p>
      )}
      <ul className="pa-revets-list">
        {items.map((i) => (
          <li key={i.id}>
            <div>
              <strong>{i.resident_name}</strong>{i.facility_name ? ` · ${i.facility_name}` : ''}
            </div>
            <div className={i.overdue ? 'pa-revets-due stop' : 'pa-revets-due'}>
              Medicaid lost on {shortDate(i.effective_on)} · {dueWords(i)}
            </div>
            <div>Reason: {i.reason}</div>
            {canRecord && (open === i.id
              ? <>
                  <OutcomePanel item={i} onDone={(msg) => { setSaid(msg); setOpen(null); load(); if (onChanged) onChanged(); }} />
                  <button type="button" className="pa-link" onClick={() => setOpen(null)}>Cancel</button>
                </>
              : <button type="button" className="pa-open" onClick={() => { setOpen(i.id); setSaid(null); }}>Record the re-vet outcome</button>)}
          </li>
        ))}
      </ul>
    </section>
  );
}
